import { uuidModule } from "@/modules/uuid";
import {
  SpaceAccountAppCalloutModelData,
  SpaceAccountAppCalloutUpsertedSyncUpdateValue,
} from "@/store/app-callout/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { IEnableSpaceAccountAppCalloutDismissalOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";

export class EnableSpaceAccountAppCalloutDismissalOperation extends BaseSyncOperation<IEnableSpaceAccountAppCalloutDismissalOperation> {
  get operationKind(): SyncOperationKind {
    return "ENABLE_SPACE_ACCOUNT_APP_CALLOUT_DISMISSAL";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const spaceAccountAppCalloutUpdate = this.generateEnableDismissalUpdate();

    if (!spaceAccountAppCalloutUpdate) {
      return [];
    }

    return [spaceAccountAppCalloutUpdate];
  }

  async triggerRecompute() {
    await this.store.spaceAccountAppCallouts.recompute(this.payload.space_account_app_callout_id);
  }

  protected generateEnableDismissalUpdate(): OptimisticSyncUpdate<SpaceAccountAppCalloutModelData> | null {
    const observable = this.store.spaceAccountAppCallouts.get(
      this.payload.space_account_app_callout_id
    );

    if (!observable) {
      return null;
    }

    const value: SpaceAccountAppCalloutUpsertedSyncUpdateValue = {
      model_id: this.payload.space_account_app_callout_id,
      model_kind: "SPACE_ACCOUNT_APP_CALLOUT",
      model_version: observable.modelVersion,
      model_data: {
        callout_kind: observable.calloutKind,
        is_dismissible: true,
        is_dismissal_enabled: true,
        is_active: observable.isActive,
        start_at: observable.startAt?.toISO() ?? null,
        end_at: observable.endAt?.toISO() ?? null,
        target_platform_kinds: observable.targetPlatformKinds,
        required_feature_flag: observable.requiredFeatureFlag,
        icon_kind: observable.iconKind,
        icon_position: observable.iconPosition,
        primary_label: observable.primaryLabel,
        secondary_label: observable.secondaryLabel,
        inline_action_label: observable.inlineActionLabel,
        inline_action_url: observable.inlineActionUrl,
        primary_action_label: observable.primaryActionLabel,
        primary_action_url: observable.primaryActionUrl,
        primary_action_icon_kind: observable.primaryActionIconKind,
        primary_action_button_variant: observable.primaryActionButtonVariant,
        secondary_action_label: observable.secondaryActionLabel,
        secondary_action_url: observable.secondaryActionUrl,
        secondary_action_icon_kind: observable.secondaryActionIconKind,
        tertiary_action_label: observable.tertiaryActionLabel,
        tertiary_action_url: observable.tertiaryActionUrl,
        tertiary_action_icon_kind: observable.tertiaryActionIconKind,
        space_account_id: observable.spaceAccountId,
        app_callout_id: observable.appCalloutId,
        is_dismissed: observable.isDismissed,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    return {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value,
    };
  }
}
